import { api } from "./api";

export interface CompanySettings {
  legal_name: string;
  brand_name: string;
  contact_email: string;
  contact_phone: string;
  timezone: string;
  currency: string;
  address: string;
  tax_id: string | null;
}

export interface SSOSettings {
  google_enabled: boolean;
  google_client_id: string | null;
  google_hd_domain: string | null;
  microsoft_enabled: boolean;
  microsoft_tenant_id: string | null;
}

export interface BusinessParams {
  default_cancel_window_hours: number;
  default_b2b_markup_pct: number;
  default_api_markup_pct: number;
  over_sell_max_pct: number;
  yellow_alert_threshold_pct: number;
  large_expense_threshold_cents: number;
  gps_log_retention_days: number;
  notification_quiet_start: string;
  notification_quiet_end: string;
}

export interface NotificationChannelConfig {
  channel: "email" | "sms" | "wechat" | "app";
  enabled: boolean;
  sender: string | null;
  api_key_ref: string | null;
}

export interface NotificationTemplate {
  code: string;
  label_cn: string;
  icon: string;
  channels: string[];
  audience: "customer" | "staff" | "owner";
  channel: "email" | "sms" | "wechat" | "app";
  subject: string | null;
  body: string;
  enabled: boolean;
  updated_at: string | null;
}

export interface QuietHours {
  start: string;
  end: string;
  enabled: boolean;
  exclude_severity: ("info" | "warning" | "danger")[];
}

export interface FeatureFlagItem {
  key: string;
  label_cn: string;
  enabled: boolean;
  category: string;
  warning: string | null;
}

export interface FeatureFlagsPayload {
  flags: FeatureFlagItem[];
}

export interface BrandingRead {
  legal_name: string;
  brand_name: string;
  logo_url: string | null;
}

export interface GeneralSettings {
  legal_name: string;
  brand_name: string;
  logo_url: string | null;
  name_cn: string;
  name_en: string;
  tax_id: string | null;
  registered_address: string;
  contact_phone: string;
  contact_email: string;
  website: string | null;
  business_license: string | null;
  currency: string;
  secondary_currency: string;
  fx_source: string;
  fiscal_close_day: number;
  timezone: string;
  working_hours: string;
  /** GOLIVE-1 C · 同行站下单后「等待确认」占位时长（分钟，10 ~ 2880，默认 120） */
  partner_order_hold_minutes?: number;
  /** CONFIRM-1 · 「人工确认 / 未设置确认方式」产品的待确认单占位时长（分钟，10 ~ 4320，默认 1440 = 24 小时） */
  manual_order_hold_minutes?: number;
  /** CREDIT-1 · 同行结算全局默认（单个同行可在「信用 & 结算」里单独覆盖）：月结 = 出发月次月 N 号到期 / 单结 = 出发后 N 天到期 / 逾期超过 N 天自动停新单 */
  partner_monthly_due_day?: number;
  partner_per_order_due_days?: number;
  partner_block_after_overdue_days?: number;
  order_no_prefix: string;
  invoice_no_prefix: string;
  address: string;
  // PATCH-1 件1 · 公司 CUA 号（凭证抬头用·默认 None=未填）
  cua_number: string | null;
  // ═ COMPANY-STRIP BEGIN ═ COMPANY-CUT-1 · 订阅版顶栏用的两个地址
  /** SAAS-2 · 帮助教程地址（订阅版顶栏 / 首页右下角 / 应用中心）；空 = 入口不显示。老接口没有这个键时按 undefined 原样回传 */
  help_center_url?: string | null;
  /** SAAS-2b · 手机站地址（订阅版顶栏「复制手机站地址 / 二维码」读它）；空 = 那一项不显示 */
  mobile_site_url?: string | null;
  // ═ COMPANY-STRIP END ═
}

// ORDSTAT Part C · 出团确认单模板
export interface ConfirmationTermItem { zh: string; en: string }
export interface ConfirmationAfterSalesBlock {
  wechat: string; cn_phone: string; us_phone: string; whatsapp: string; email: string;
}
export interface ConfirmationTemplateSettings {
  pickup_time_note: string;
  after_sales_block: ConfirmationAfterSalesBlock;
  footer_phone: string;
  terms: ConfirmationTermItem[];
}

// CANCELPOL · 全公司统一取消条款（tenant_settings.blob['cancellation_policy']）。
// 阶梯语义：days_before = 出发日前 N 天（含）以上适用该比例；按 days_before 降序求值。
/** CHANAV-3 · 渠道余位计入酒店和门票（总开关） */
export interface ChannelSeatsSettings {
  include_hotel_ticket: boolean;
  updated_at?: string | null;
  repushed_events?: number | null;   // 仅 PUT 返回：本次切换入队的余位重推事件数
}

export interface CancellationTier { days_before: number; refund_pct: number }
export interface CancellationPolicySettings {
  tiers: CancellationTier[];
  note: string | null;
  updated_at?: string | null;
}

// ═ COMPANY-STRIP BEGIN ═ COMPANY-CUT-1 · 主题层（THEME-1）
/** THEME-1 · 主题设置（令牌由后端算，前端只写变量）。 */
export interface ThemeSettings {
  preset: "classic" | "pine";
  brand: string;
  accent: string;
  brand_custom: boolean;
  accent_custom: boolean;
  defaults: { brand?: string; accent?: string };
  admin_layout: "sidebar" | "topnav";
  partner_layout: "classic" | "bms";
  edition?: "enterprise" | "subscription";   // SAAS-1 · 版本类型：企业版（默认 = 现在的样子）/ 订阅版（个人站专属内容）
  tokens: Record<string, string>;
}
export interface ThemePatch {
  preset?: "classic" | "pine";
  /** null = 清掉覆盖、回到预设自带 */
  brand?: string | null;
  accent?: string | null;
  admin_layout?: "sidebar" | "topnav";
  partner_layout?: "classic" | "bms";
  edition?: "enterprise" | "subscription";
}
export interface ThemePreview {
  tokens: Record<string, string>;
  contrast: { brand?: number; accent?: number };
  error: string | null;
}
// ═ COMPANY-STRIP END ═

export const settingsApi = {
  // ═ COMPANY-STRIP BEGIN ═
  /** THEME-1 · 主题设置：GET 全员可读；PATCH 老板 / 技术管理员（对比度不够 → 422 + 提示）；preview 只算不存 */
  getTheme: () => api<ThemeSettings>("/api/v1/settings/theme"),
  patchTheme: (payload: ThemePatch) => api<ThemeSettings>("/api/v1/settings/theme", { method: "PATCH", body: JSON.stringify(payload) }),
  previewTheme: (payload: ThemePatch) => api<ThemePreview>("/api/v1/settings/theme:preview", { method: "POST", body: JSON.stringify(payload) }),
  // ═ COMPANY-STRIP END ═
  /** 品牌（公司名+logo）· 任何登录用户可读 · sidebar/凭证中心化读取 */
  getBranding: () => api<BrandingRead>("/api/v1/settings/branding"),
  /** ORDSTAT Part C · 出团确认单模板（条款 + 售后块 + 页尾）*/
  getConfirmationTemplate: () =>
    api<ConfirmationTemplateSettings>("/api/v1/settings/confirmation-template"),
  putConfirmationTemplate: (payload: ConfirmationTemplateSettings) =>
    api<ConfirmationTemplateSettings>("/api/v1/settings/confirmation-template", {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  /** CANCELPOL · 全公司统一取消条款 · GET 任何登录用户可读，PUT 仅设置管理员 */
  getCancellationPolicy: () =>
    api<CancellationPolicySettings>("/api/v1/settings/cancellation-policy"),
  putCancellationPolicy: (payload: CancellationPolicySettings) =>
    api<CancellationPolicySettings>("/api/v1/settings/cancellation-policy", {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  /** CHANAV-3 · 渠道余位是否计入酒店和门票（总开关，默认关）· GET system.settings.view，PUT 仅老板 / 技术管理员；切换会全量重推余位 */
  getChannelSeats: () => api<ChannelSeatsSettings>("/api/v1/settings/channel-seats"),
  putChannelSeats: (include_hotel_ticket: boolean) =>
    api<ChannelSeatsSettings>("/api/v1/settings/channel-seats", { method: "PUT", body: JSON.stringify({ include_hotel_ticket }) }),
  /** 通用设置（落库·含 logo_url）· 仅设置管理员 */
  getGeneral: () => api<GeneralSettings>("/api/v1/settings/general"),
  patchGeneral: (payload: GeneralSettings) =>
    api<GeneralSettings>("/api/v1/settings/general", { method: "PATCH", body: JSON.stringify(payload) }),
  getFeatureFlags: () => api<FeatureFlagsPayload>("/api/v1/settings/feature-flags"),
  patchFeatureFlags: (payload: FeatureFlagsPayload) =>
    api<FeatureFlagsPayload>("/api/v1/settings/feature-flags", {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),


  getCompany: () => api<CompanySettings>("/api/v1/settings/company"),
  patchCompany: (payload: CompanySettings) =>
    api<CompanySettings>("/api/v1/settings/company", {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  getSso: () => api<SSOSettings>("/api/v1/settings/sso"),
  patchSso: (payload: SSOSettings) =>
    api<SSOSettings>("/api/v1/settings/sso", { method: "PATCH", body: JSON.stringify(payload) }),

  getBusiness: () => api<BusinessParams>("/api/v1/settings/business-params"),
  patchBusiness: (payload: BusinessParams) =>
    api<BusinessParams>("/api/v1/settings/business-params", {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  getChannels: () => api<NotificationChannelConfig[]>("/api/v1/settings/notifications/channels"),
  patchChannels: (payload: NotificationChannelConfig[]) =>
    api<NotificationChannelConfig[]>("/api/v1/settings/notifications/channels", {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  getTemplates: () => api<NotificationTemplate[]>("/api/v1/settings/notifications/templates"),
  patchTemplates: (payload: NotificationTemplate[]) =>
    api<NotificationTemplate[]>("/api/v1/settings/notifications/templates", {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  getQuiet: () => api<QuietHours>("/api/v1/settings/notifications/quiet-hours"),
  patchQuiet: (payload: QuietHours) =>
    api<QuietHours>("/api/v1/settings/notifications/quiet-hours", {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
};
